import { api } from "@/lib/api";
import { useQueryClient } from "@tanstack/react-query";
import { ApiError } from "@/lib/api";
import { Brand } from "./use-brand-ads";

export interface Project {
  id: number;
  name: string;
  brand: Brand;
  competitors?: Brand[];
  summary?: string;
}

interface ProjectSettings {
  competitor_new_ads_notifications_email: boolean;
  strategy_update_notifications_email: boolean;
}

interface UpdateProjectSettingsData {
  competitor_new_ads_notifications_email?: boolean;
  strategy_update_notifications_email?: boolean;
}

interface CreateProjectData {
  name: string;
  brand_id: number;
}

interface UpdateProjectData {
  name?: string;
  brand_id?: number;
}

const PROJECTS_QUERY_KEY = "/api/projects/";

export function useProjects() {
  return api.useGet<Project[]>(PROJECTS_QUERY_KEY);
}

export function useProject(id: string) {
  if (!id) {
    return {
      data: null,
      isLoading: false,
      isError: false,
    };
  }
  return api.useGet<Project>(`/api/projects/${id}/`);
}

export function useCreateProject() {
  const queryClient = useQueryClient();
  return api.usePost<Project, CreateProjectData>("/api/projects/create/", {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PROJECTS_QUERY_KEY] });
    },
  });
}

export function useUpdateProject(projectId: number) {
  const queryClient = useQueryClient();
  return api.usePut<Project, UpdateProjectData>(
    `/api/projects/${projectId}/update/`,
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [PROJECTS_QUERY_KEY] });
      },
    }
  );
}

export function useDeleteProject(projectId: number) {
  const queryClient = useQueryClient();
  return api.useDelete(`/api/projects/${projectId}/delete/`, {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PROJECTS_QUERY_KEY] });
    },
    onError: (error: ApiError) => {
      console.error("Failed to delete project:", error);
      throw error;
    },
  });
}

export function useAddCompetitor(projectId: number) {
  const queryClient = useQueryClient();
  return api.usePost<Project, { brand_id: number }>(
    `/api/projects/${projectId}/competitors/add/`,
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [PROJECTS_QUERY_KEY] });
        queryClient.invalidateQueries({
          queryKey: [`/api/projects/${projectId}/`],
        });
      },
    }
  );
}

export function useRemoveCompetitor(projectId: number, brandId: number) {
  const queryClient = useQueryClient();
  return api.useDelete<Project>(
    `/api/projects/${projectId}/competitors/${brandId}/remove/`,
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [PROJECTS_QUERY_KEY] });
        queryClient.invalidateQueries({
          queryKey: [`/api/projects/${projectId}/`],
        });
      },
    }
  );
}

export function useProjectSettings(projectId: number) {
  return api.useGet<ProjectSettings>(`/api/projects/${projectId}/settings/`);
}

export function useUpdateProjectSettings(projectId: number) {
  const queryClient = useQueryClient();
  return api.usePut<ProjectSettings, UpdateProjectSettingsData>(
    `/api/projects/${projectId}/settings/`,
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [PROJECTS_QUERY_KEY] });
      },
    }
  );
}
